import { useEffect, useState } from "react";
import { Loader2, Send as SendIcon, Terminal } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtTime, STATUS } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function SlotPicker({ value, onChange }) {
  const [names, setNames] = useState([]);
  useEffect(() => { api.get("/sim-names").then((r) => setNames(r.data)); }, []);
  return (
    <Select value={value?.key || ""} onValueChange={(k) => onChange(names.find((n) => n.key === k))}>
      <SelectTrigger data-testid="send-slot-select" className="bg-white h-11"><SelectValue placeholder="Pilih nama SIM…" /></SelectTrigger>
      <SelectContent className="max-h-80">
        {names.map((n) => (
          <SelectItem key={n.key} value={n.key}>
            <span className="font-bold text-emerald-700">{n.name}</span>{n.company && <span className="ml-1.5 text-[10px] font-bold text-slate-500">{n.company}</span>}
            <span className="ml-2 font-mono text-xs text-slate-500">{n.device_name} · {n.port}.{n.slot} · {STATUS[n.status]?.label}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SmsForm({ slot }) {
  const [to, setTo] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!slot) return toast.error("Pilih nama SIM dulu");
    setBusy(true);
    try {
      await api.post("/send/sms", { device_id: slot.device_id, port: slot.port, slot: slot.slot, to, text });
      toast.success(`SMS dikirim dari ${slot.name} ke ${to}`);
      setText("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      <Input data-testid="send-sms-to-input" placeholder="Nomor tujuan, mis. 08123456789" value={to} onChange={(e) => setTo(e.target.value)} className="bg-white h-11" />
      <Textarea data-testid="send-sms-text-input" placeholder="Isi pesan" rows={5} value={text} onChange={(e) => setText(e.target.value)} className="bg-white" />
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono text-slate-500">{text.length} karakter</span>
        <Button data-testid="send-sms-submit-button" disabled={busy} onClick={send} className="bg-emerald-500 hover:bg-emerald-600 text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <SendIcon className="h-4 w-4 mr-2" />}Kirim SMS
        </Button>
      </div>
    </div>
  );
}

function UssdForm({ slot }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const loadLog = () => slot && api.get("/ussd-log", { params: { device_id: slot.device_id, port: slot.port, slot: slot.slot } }).then((r) => setLog(r.data));
  useEffect(() => { loadLog(); }, [slot?.key]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    if (!slot) return toast.error("Pilih nama SIM dulu");
    setBusy(true);
    try {
      await api.post("/send/ussd", { device_id: slot.device_id, port: slot.port, slot: slot.slot, code });
      loadLog();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input data-testid="ussd-code-input" placeholder="mis. *888#" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} className="bg-white h-11 font-mono" />
        <Button data-testid="execute-ussd-submit-button" disabled={busy || !code} onClick={run} className="h-11 bg-emerald-500 hover:bg-emerald-600 text-white">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kirim"}
        </Button>
      </div>
      <div data-testid="ussd-terminal" className="rounded-md border border-slate-200 bg-slate-50 p-4 font-mono text-xs min-h-[200px] max-h-[360px] overflow-y-auto space-y-3">
        {busy && <div className="text-amber-700 animate-pulse">&gt; menunggu balasan operator…</div>}
        {log.map((l) => (
          <div key={l.id}>
            <div className="text-slate-500">[{fmtTime(l.created_at)}] {l.user} &gt; <span className="text-sky-700">{l.code}</span></div>
            <div className="text-emerald-600 whitespace-pre-wrap">{l.reply || "(tanpa balasan)"}</div>
          </div>
        ))}
        {!log.length && !busy && <div className="text-slate-400">Belum ada riwayat USSD untuk slot ini.</div>}
      </div>
    </div>
  );
}

export default function Send() {
  const [slot, setSlot] = useState(null);
  const { can } = useAuth();
  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500">Kirim</p>
        <h1 className="font-display text-4xl font-bold">SMS & USSD</h1>
      </div>
      <SlotPicker value={slot} onChange={setSlot} />
      {slot && slot.status !== "active" && (
        <p className="text-xs text-amber-700">Slot {slot.name} sedang {STATUS[slot.status]?.label?.toLowerCase()}. Perintah hanya berhasil jika slot ini aktif di port-nya.</p>
      )}
      <Tabs defaultValue={can("send_sms") ? "sms" : "ussd"}>
        <TabsList className="bg-white">
          <TabsTrigger data-testid="tab-sms" value="sms" disabled={!can("send_sms")}><SendIcon className="h-4 w-4 mr-2" />SMS</TabsTrigger>
          <TabsTrigger data-testid="tab-ussd" value="ussd" disabled={!can("send_ussd")}><Terminal className="h-4 w-4 mr-2" />USSD</TabsTrigger>
        </TabsList>
        <TabsContent value="sms" className="mt-4"><SmsForm slot={slot} /></TabsContent>
        <TabsContent value="ussd" className="mt-4"><UssdForm slot={slot} /></TabsContent>
      </Tabs>
    </div>
  );
}
